import { Autocomplete, Chip, CircularProgress, TextField } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { CompanySummary, findCompany } from '../../lib/companies';
import { useCompanyMatches } from '../../lib/useCompanyMatches';
import { countryOf, matchCountries, Country } from './countries';

interface CodesFilterProps {
  value: string[];
  onChange: (value: string[]) => void;
}

const FIELD_SX = { flex: '1 1 260px', maxWidth: 380 };

/** Companies by full UUID or any 3 characters of the name; templates assigned to any picked one are listed. */
export const CompanyFilter = ({ value, onChange }: CodesFilterProps) => {
  const [input, setInput] = useState('');
  const [names, setNames] = useState<Record<string, string>>({});
  const { matches, total, loading, error, byUuid } = useCompanyMatches(input);

  // Companies named only by the address (a shared link, the back button) are looked up once.
  const unnamedKey = value.filter((uuid) => names[uuid] === undefined).join(',');
  useEffect(() => {
    if (!unnamedKey) return undefined;
    let cancelled = false;
    Promise.all(unnamedKey.split(',').map((uuid) => findCompany(uuid).catch(() => null))).then((found) => {
      if (cancelled) return;
      setNames((previous) => {
        const next = { ...previous };
        unnamedKey.split(',').forEach((uuid, index) => {
          next[uuid] = found[index]?.name ?? '(unknown company)';
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [unnamedKey]);

  const selected: CompanySummary[] = value.map((uuid) => ({ uuid, name: names[uuid] ?? null }));
  const options = matches.filter((company) => !value.includes(company.uuid));
  let helper = 'UUID or 3 characters of the name; * skips any text';
  if (error) helper = error;
  else if (!loading && total !== null && total > matches.length) helper = `${matches.length} of ${total} companies; type more to narrow`;

  return (
    <Autocomplete
      multiple
      size="small"
      sx={FIELD_SX}
      options={options}
      value={selected}
      filterOptions={(all) => all}
      inputValue={input}
      onInputChange={(_, next, reason) => {
        if (reason !== 'reset') setInput(next);
      }}
      onChange={(_, picked) => {
        // A chip whose name is still loading stays out, or its lookup would be cancelled.
        const named = picked.filter((company) => company.name !== null);
        setNames((previous) => ({ ...previous, ...Object.fromEntries(named.map((company) => [company.uuid, company.name ?? ''])) }));
        onChange(picked.map((company) => company.uuid));
        setInput('');
      }}
      isOptionEqualToValue={(option, chosen) => option.uuid === chosen.uuid}
      getOptionLabel={(company) => company.name ?? company.uuid}
      renderOption={(props, company) => (
        <li {...props} key={company.uuid}>
          {`${company.name || '(no name)'} · ${company.uuid.slice(0, 8)}`}
        </li>
      )}
      renderTags={(chosen, getTagProps) =>
        chosen.map((company, index) => <Chip {...getTagProps({ index })} key={company.uuid} size="small" label={company.name === null ? company.uuid.slice(0, 8) : `${company.name || '(no name)'} · ${company.uuid.slice(0, 8)}`} title={company.uuid} />)
      }
      loading={loading}
      noOptionsText={byUuid ? 'No company with this UUID' : 'No company with this name'}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Company"
          helperText={helper}
          error={Boolean(error)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
};

/** Countries by 3 letters of the name or the exact 2-letter code; templates listing any picked one are listed. */
export const CountryFilter = ({ value, onChange }: CodesFilterProps) => {
  const [input, setInput] = useState('');
  const selected = useMemo(() => value.map(countryOf), [value]);
  const options = matchCountries(input).filter((country) => !value.includes(country.code));

  return (
    <Autocomplete
      multiple
      size="small"
      sx={FIELD_SX}
      options={options}
      value={selected}
      filterOptions={(all) => all}
      inputValue={input}
      onInputChange={(_, next, reason) => {
        if (reason !== 'reset') setInput(next);
      }}
      onChange={(_, picked: Country[]) => {
        onChange(picked.map((country) => country.code));
        setInput('');
      }}
      isOptionEqualToValue={(option, chosen) => option.code === chosen.code}
      getOptionLabel={(country) => `${country.name} (${country.code})`}
      renderTags={(chosen, getTagProps) => chosen.map((country, index) => <Chip {...getTagProps({ index })} key={country.code} size="small" label={`${country.name} (${country.code})`} />)}
      noOptionsText={input.trim().length < 2 ? 'Type 3 letters of a country, or its code' : 'No country matches'}
      renderInput={(params) => <TextField {...params} label="Country" helperText="3 letters of the name, or the 2-letter code" />}
    />
  );
};
